import './Cover.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import { classNames as cn, getImageSizeByUrl, rnbr } from '$utils';
import { BottomBar, Button, Form, FormItem, Header, ScrollView } from '$uikit';
import { useTranslator } from '$hooks';
import { profileActions, profileSelector } from '$store/profile';
import { Loader } from '$shared/components';
import { toastActions } from '$store/toast';
import { IcUpload28 } from '$assets';
import { useRoute } from 'react-router5';

export const Cover: FC = () => {
  const t = useTranslator();
  const { route } = useRoute();
  const dispatch = useDispatch();
  const { coverUrl } = useSelector(profileSelector);
  const [{ x, y }, api] = useSpring(() => ({ x: 0, y: 0 }));
  const boundsRef = useRef<HTMLLabelElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploaded, setIsUploaded] = useState(false);
  const [isWide, setIsWide] = useState(false);

  const imgWidth = imageRef.current?.clientWidth || 0;
  const boundsWidth = boundsRef.current?.clientWidth || 0;
  const imgHeight = imageRef.current?.clientHeight || 0;
  const boundsHeight = boundsRef.current?.clientHeight || 0;

  const bind = useDrag(
    ({ down, offset: [ox, oy] }) => {
      api.start({ x: ox, y: oy, immediate: down });
    },
    {
      bounds: {
        top: boundsHeight - imgHeight,
        bottom: 0,
        right: 0,
        left: boundsWidth - imgWidth,
      },
    },
  );

  useEffect(() => {
    if (coverUrl !== null) {
      getImageSizeByUrl(coverUrl, false).then(({ width, height }) => {
        if (width / height > 16 / 9) {
          setIsWide(true);
        } else {
          setIsWide(false);
        }
      });
    }
  }, [coverUrl]);

  useEffect(() => {
    return () => {
      dispatch(profileActions.setCoverUrl(''));
      setIsUploaded(false);
    };
  }, []);

  useEffect(() => {
    if (coverUrl) {
      if (imageRef.current) {
        imageRef.current.src = coverUrl;
        imageRef.current.onload = () => {
          setIsLoading(false);
          setIsUploaded(true);
        };
      }
    }
  }, [coverUrl, imageRef]);

  const handleSave = useCallback(() => {
    const offsetY = y.get() / (imageRef.current?.clientHeight || 0);
    const offsetX = x.get() / (imageRef.current?.clientWidth || 0);
    if (coverUrl) {
      dispatch(
        profileActions.uploadCover({
          offsetY,
          offsetX,
          isWide,
          coverUrl,
          isAdCover: !!route.params?.isAdCover,
        }),
      );
    }
  }, [dispatch, y, x, isWide, coverUrl]);

  const handleChange = useCallback(
    ({ target }) => {
      const file = target.files[0];
      const url = window.URL.createObjectURL(file);

      getImageSizeByUrl(url, false).then(({ width }) => {
        if (width < 620) {
          dispatch(toastActions.setToastFail(t('profile_change_cover_error_width')));
          return;
        }
        dispatch(profileActions.setCoverUrl(url));
        api.start({ x: 0, y: 0, immediate: true });
      });
    },
    [dispatch],
  );

  return (
    <>
      <Header showBackButton>{t('profile_edit_cover')}</Header>
      <ScrollView className="Cover">
        <label
          className={cn('Cover__editor', {
            loading: isLoading,
            uploaded: uploaded,
            wide: isWide,
            big_padding_top: true,
            small_padding_top: false,
          })}
          {...bind()}
          ref={boundsRef}
        >
          <animated.img ref={imageRef} src="" alt="cover" style={{ x, y }} />
          {isLoading && <Loader />}

          {!uploaded && (
            <>
              <div className={'Cover__information'}>
                <IcUpload28 />
                <div className="Cover__text">{t('profile_upload_cover')}</div>
              </div>
              <input
                className={'Cover__attach'}
                type="file"
                accept="image/*"
                onChange={handleChange}
              />
            </>
          )}
        </label>
        <Form>
          <FormItem description={rnbr(t('profile_edit_cover_caption'))} />
          {coverUrl && (
            <FormItem>
              <Button className="Cover__inputButton" stretched mode="secondary">
                <input type="file" accept="image/*" onChange={handleChange} />
                {t('profile_change_cover')}
              </Button>
            </FormItem>
          )}
        </Form>
      </ScrollView>
      <BottomBar>
        <Button disabled={!uploaded} stretched onClick={handleSave}>
          {t('profile_edit_cover_save')}
        </Button>
      </BottomBar>
    </>
  );
};
